<script lang="ts" setup>
import { computed } from 'vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { type BasicColor, type OpacityLevel, getBgClass } from '@tailwind-preset/colors';

export interface VStatusTagProps {
  color?: BasicColor;
  inverted?: boolean;
  opacity?: OpacityLevel;
  size?: keyof typeof sizeClasses;
  hideIcon?: boolean;
}

const props = withDefaults(defineProps<VStatusTagProps>(), {
  color: 'grey',
  opacity: undefined,
  size: 'md',
});

type ColorClasses = {
  regular: string;
  inverted: string;
};

const colors: Record<BasicColor, ColorClasses> = {
  black: { regular: 'text-white', inverted: 'text-black' },
  blue: { regular: 'text-white', inverted: 'text-blue' },
  grey: { regular: 'text-grey', inverted: 'text-grey-200' },
  green: { regular: 'text-black', inverted: 'text-green' },
  orange: { regular: 'text-white', inverted: 'text-orange' },
  purple: { regular: 'text-white', inverted: 'text-purple' },
  red: { regular: 'text-white', inverted: 'text-red' },
  teal: { regular: 'text-white', inverted: 'text-teal' },
  yellow: { regular: 'text-black', inverted: 'text-yellow' },
} as const;

const colorClasses = computed(() =>
  props.inverted
    ? colors[props.color].inverted
    : `${colors[props.color].regular} ${getBgClass(props.color, props.opacity)}`,
);

const sizeClasses = {
  sm: 'rounded px-1.5 py-0.5',
  md: 'rounded-md px-2.5 py-1',
  lg: 'rounded-md px-4 h-11',
};
</script>

<template>
  <span
    :class="[
      'inline-flex items-center gap-ch text-sm font-bold',
      sizeClasses[props.size],
      {
        'border border-grey-200 bg-white': props.inverted,
        [colorClasses]: !props.inverted,
      },
    ]"
  >
    <FontAwesomeIcon
      v-if="props.inverted && !props.hideIcon"
      size="2xs"
      icon="fa fa-circle"
      :class="colorClasses"
    />
    <slot></slot>
  </span>
</template>
